import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { mix } from 'polished';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ArchiveIcon, HistoryIcon } from '@treams/treams-icons';
import { useHistory } from '@treams/treams-routes';
import { Author, Container, Paragraph, Tags, Title, Callout, Todo } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import QuillText from 'packages/kakapo-ui-kit/components/editors/QuillText';

import Subgoals, { SubgoalsContext } from './subgoals/Subgoals';
import SubgoalsContainer from './subgoals/SubgoalsContainer';

function FeasibilityTag(props: { feasibility: graphql.GoalFeasibility }) {
	const { t } = useTranslation();

	const feasibilityMap = {
		[graphql.GoalFeasibility.CloseCall]: {
			label: t('goals.show.feasibility.closeCall'),
			color: 'yellow',
		},
		[graphql.GoalFeasibility.WillMakeIt]: {
			label: t('goals.show.feasibility.willMakeIt'),
			color: 'green',
		},
		[graphql.GoalFeasibility.WontMakeIt]: {
			label: t('goals.show.feasibility.wontMakeIt'),
			color: 'red',
		},
	};

	const { feasibility } = props;

	return (
		<Tags.Item
			status={feasibilityMap[feasibility].color}
			label={t('goals.show.feasibility.label')}
			value={feasibilityMap[feasibility].label}
		/>
	);
}

const Strikethrough = styled.span`
	background: ${(props) => mix(0.75, props.theme.colors.white, props.theme.colors.green)};
	color: ${(props) => props.theme.colors.green};
`;

export default function GoalRevision(props: { userID?: string; goalID: string; revision: string }) {
	const history = useHistory();
	const secret = new URLSearchParams(history.location.search).get('secret');

	const query = graphql.useGoalRevisionQuery({
		variables: { id: props.goalID, revision: props.revision, secret: secret },
	});

	const { localize } = useLocalize();
	const { t } = useTranslation();

	const revision = query.data?.goalRevision;

	const isArchived = !!revision?.archivedAt;

	return (
		<>
			<Container py={4}>
				{isArchived ? (
					<Callout
						mb={3}
						color="red"
						icon={<ArchiveIcon />}
						title={t('goals.show.archived.title')}
						description={t('goals.show.archived.description')}
					/>
				) : (
					<Callout
						mb={3}
						color="blue"
						icon={<HistoryIcon />}
						title={t('goals.show.revision.title')}
						description={t('goals.show.revision.description')}
					/>
				)}
				{revision ? (
					<Box mt={4}>
						<Flex alignItems="center" mb={3}>
							<Box flexGrow={0} flexShrink={0} mr={3}>
								<Todo checked={revision.progress === 1.0} />
							</Box>
							<Title>
								{revision.completedAt ? (
									<Strikethrough>{localize(revision.title).text}</Strikethrough>
								) : (
									localize(revision.title).text
								)}
							</Title>
						</Flex>
						<Tags mb={3}>
							{revision?.feasibility && <FeasibilityTag feasibility={revision.feasibility} />}
							{revision?.dueDate && (
								<Tags.Item
									label={t('goals.show.dueDate.label')}
									value={moment(revision?.dueDate).format('D MMM YYYY')}
								/>
							)}
							{revision?.competency && (
								<Tags.Item
									label={t('goals.show.competency.label')}
									value={localize(revision?.competency.competency.title).text}
								/>
							)}
						</Tags>
						<Box mb={3}>
							<QuillText quill={revision.description} />
						</Box>
					</Box>
				) : (
					<Box mt={4}>
						<Title.Skeleton mb={3} />
						<Paragraph.Skeleton mb={3} />
						<Author.Skeleton />
					</Box>
				)}
			</Container>
			<SubgoalsContainer>
				{query.data ? (
					<SubgoalsContext goal={revision as unknown as graphql.Goal} />
				) : (
					<Subgoals.Skeleton />
				)}
			</SubgoalsContainer>
		</>
	);
}
